import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import Config from 'src/common/Config';
import type { DocumentEditOwner } from '../documentOwner';

const API_URL = Config.getApiUrl();

type InlineDocumentEditPayload = DocumentEditOwner & {
  documentContent: string;
  editRequest: string;
  signal?: AbortSignal;
};

export function useInlineDocumentEdit(documentId?: string) {
  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation({
    mutationFn: async (payload: InlineDocumentEditPayload) => {
      if (!documentId) throw new Error('documentId is required');

      const { documentContent, editRequest, signal, ...owner } = payload;
      const strippedContent = documentContent
        .replace(/<suggestion[^>]*>/g, '')
        .replace(/<\/suggestion>/g, '');

      const response = await fetch(`${API_URL}/documents/${documentId}/inline-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentContent: strippedContent,
          editRequest,
          ...owner,
        }),
        credentials: 'include',
        signal,
      });

      if (response.status < 200 || response.status > 299) throw response;

      const data = await response.json();
      const rawMarkdown = data.markdown as string;

      return rawMarkdown.replaceAll('<replacement>', '').replaceAll('</replacement>', '');
    },
  });

  const inlineEdit = useCallback(
    (payload: InlineDocumentEditPayload) => mutateAsync(payload),
    [mutateAsync],
  );

  return {
    inlineEdit,
    error,
    isLoading,
    reset,
  };
}
